import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Coins, TrendingUp } from 'lucide-react';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';

interface CostBucket {
  readonly service: string;
  readonly currency: string;
  readonly mtdAmount: number;
}

interface CostsResponse {
  readonly configured: boolean;
  readonly mtdTotal: number;
  readonly projectedMonthEnd: number;
  readonly currency: string;
  readonly byService: readonly CostBucket[];
  readonly note: string | null;
}

function fmtMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function Costs() {
  const query = useQuery({
    queryKey: queryKeys.costs(),
    queryFn: async () => {
      const res = await apiClient.get<CostsResponse>('/owner/costs');
      return res.data;
    },
  });

  const data = query.data;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Costs</h1>
        <p className="nw-page-header__subtitle">
          GCP spend month-to-date and projected month-end. Per-service breakdown for budget hot spots.
        </p>
      </header>

      {data && !data.configured && (
        <div className="nw-placeholder">
          <h2 className="nw-placeholder__title">Cost snapshot not set</h2>
          <p>{data.note}</p>
        </div>
      )}

      {data?.configured && (
        <>
          <div className="nw-kpi-grid">
            <div className="nw-kpi">
              <div className="nw-kpi__head">
                <span className="nw-kpi__icon">
                  <Coins size={18} strokeWidth={2} />
                </span>
                <span className="nw-kpi__label">MTD spend</span>
              </div>
              <div className="nw-kpi__value">
                {fmtMoney(data.mtdTotal, data.currency)}
              </div>
            </div>
            <div className="nw-kpi">
              <div className="nw-kpi__head">
                <span className="nw-kpi__icon">
                  <TrendingUp size={18} strokeWidth={2} />
                </span>
                <span className="nw-kpi__label">Projected month-end</span>
              </div>
              <div className="nw-kpi__value">
                {fmtMoney(data.projectedMonthEnd, data.currency)}
              </div>
              <span className="nw-kpi__delta-note">linear extrapolation</span>
            </div>
          </div>

          <section className="nw-panel">
            <header className="nw-panel__header">
              <h2 className="nw-panel__title">By service · month-to-date</h2>
            </header>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={[...data.byService]} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid stroke="var(--divider)" strokeOpacity={0.4} />
                <XAxis type="number" stroke="var(--text-muted)" tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="service"
                  stroke="var(--text-muted)"
                  tick={{ fontSize: 12 }}
                  width={140}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--card)',
                    border: '0.5px solid var(--card-border)',
                    borderRadius: 10,
                  }}
                  formatter={(v: number) => fmtMoney(v, data.currency)}
                />
                <Bar dataKey="mtdAmount" fill="#ff5c28" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </section>
        </>
      )}
    </>
  );
}
